/**
 * Role Builder Settings — tenant-level configuration for company content blocks.
 * Manages about_company, what_we_offer, and about_role_pivot fields.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';
import {
  Page,
  PageBody,
  Alert,
  Spinner,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Textarea,
  Badge,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
} from '../../components/ui';

interface TenantConfig {
  id: number;
  about_company: string[];
  what_we_offer: string[];
  about_role_pivot: string;
  updated_at: string;
}

interface ConfigRevision {
  id: number;
  revision_number: number;
  about_company: string[];
  what_we_offer: string[];
  about_role_pivot: string;
  created_at: string;
}

function formatApiDetail(err: unknown, fallback: string): string {
  const r = err as { response?: { data?: { detail?: unknown } } };
  const d = r.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return (
      d
        .map((x) =>
          typeof x === 'string' ? x : (x as { msg?: string })?.msg || JSON.stringify(x)
        )
        .join('; ') || fallback
    );
  }
  return fallback;
}

function SubheaderBar({
  config,
  saving,
  onBack,
  onSave,
  onOpenHistory,
}: {
  config: TenantConfig | null;
  saving: boolean;
  onBack: () => void;
  onSave: () => void;
  onOpenHistory: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-gray-200 bg-white px-8 py-3 dark:border-dark-border dark:bg-dark-surface shrink-0">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="shrink-0">
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Role Builder
        </Button>
        <div className="h-5 w-px bg-gray-200 dark:bg-dark-border shrink-0" />
        <h1 className="font-title text-lg text-charcoal dark:text-gray-100">Role Builder Settings</h1>
      </div>
      <div className="flex items-center gap-3">
        {config && (
          <span className="text-xs text-muted-foreground">
            Last updated {new Date(config.updated_at).toLocaleString()}
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={onOpenHistory}>
          <ClockIcon className="w-4 h-4 mr-1.5" />
          Version history
        </Button>
        <Button variant="default" size="sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save configuration'}
        </Button>
      </div>
    </div>
  );
}

export function RoleBuilderSettingsPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [aboutCompanyText, setAboutCompanyText] = useState('');
  const [whatWeOfferText, setWhatWeOfferText] = useState('');
  const [aboutRolePivot, setAboutRolePivot] = useState('');

  const [revisions, setRevisions] = useState<ConfigRevision[]>([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AXIOS_INSTANCE.get<TenantConfig>('/api/v1/role-builder/config');
      setConfig(res.data);
      setAboutCompanyText(res.data.about_company.join('\n\n'));
      setWhatWeOfferText(res.data.what_we_offer.join('\n'));
      setAboutRolePivot(res.data.about_role_pivot);
    } catch (e: unknown) {
      setError(formatApiDetail(e, 'Failed to load configuration'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRevisions = useCallback(async () => {
    setRevisionsLoading(true);
    try {
      const res = await AXIOS_INSTANCE.get<{ items: ConfigRevision[] }>(
        '/api/v1/role-builder/config/revisions'
      );
      setRevisions(res.data.items);
    } finally {
      setRevisionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      const aboutCompany = aboutCompanyText
        .split('\n\n')
        .map((p) => p.trim())
        .filter(Boolean);
      const whatWeOffer = whatWeOfferText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const pivot = aboutRolePivot.trim();

      if (!aboutCompany.length) {
        setError('"About Company" must have at least one paragraph.');
        setSaving(false);
        return;
      }
      if (!whatWeOffer.length) {
        setError('"What We Offer" must have at least one item.');
        setSaving(false);
        return;
      }
      if (!pivot) {
        setError('"About Role Pivot" cannot be empty.');
        setSaving(false);
        return;
      }

      const res = await AXIOS_INSTANCE.put<TenantConfig>('/api/v1/role-builder/config', {
        about_company: aboutCompany,
        what_we_offer: whatWeOffer,
        about_role_pivot: pivot,
      });
      setConfig(res.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: unknown) {
      setError(formatApiDetail(e, 'Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setSheetOpen(true);
    await loadRevisions();
  };

  const restoreRevision = (revision: ConfigRevision) => {
    setAboutCompanyText(revision.about_company.join('\n\n'));
    setWhatWeOfferText(revision.what_we_offer.join('\n'));
    setAboutRolePivot(revision.about_role_pivot);
    setSheetOpen(false);
  };

  const goBack = () => navigate('/talent/role-builder');

  if (loading) {
    return (
      <Page>
        <SubheaderBar
          config={null}
          saving={false}
          onBack={goBack}
          onSave={() => {}}
          onOpenHistory={() => {}}
        />
        <PageBody>
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page>
      <SubheaderBar
        config={config}
        saving={saving}
        onBack={goBack}
        onSave={() => void handleSave()}
        onOpenHistory={() => void openHistory()}
      />
      <PageBody>
        <div className="mx-auto max-w-4xl">
          <p className="mb-6 text-sm text-muted-foreground">
            Configure the company-level content blocks used in all generated job descriptions and interview kits.
          </p>
          {error && (
            <Alert variant="error" title="Error" className="mb-6">
              {error}
            </Alert>
          )}
          {saveSuccess && (
            <Alert variant="success" title="Saved" className="mb-6">
              Configuration saved and a new revision was created.
            </Alert>
          )}
        </div>

        <div className="mx-auto max-w-4xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>About Company</CardTitle>
              <p className="text-sm text-muted-foreground">
                Separate paragraphs with a blank line. Each paragraph appears as a separate block in the "About" section of generated job descriptions.
              </p>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={8}
                value={aboutCompanyText}
                onChange={(e) => setAboutCompanyText(e.target.value)}
                placeholder="We are a technology services company..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>What We Offer</CardTitle>
              <p className="text-sm text-muted-foreground">
                One benefit per line. These appear as a bulleted list in the "What We Offer" section.
              </p>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={8}
                value={whatWeOfferText}
                onChange={(e) => setWhatWeOfferText(e.target.value)}
                placeholder="Equity in a seed-stage company..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>About Role Pivot</CardTitle>
              <p className="text-sm text-muted-foreground">
                A single paragraph that appears in the "About This Role" section, right after the role-specific description.
                This is the company pitch for why someone should consider joining now.
              </p>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={5}
                value={aboutRolePivot}
                onChange={(e) => setAboutRolePivot(e.target.value)}
                placeholder="This is a rare opportunity to join..."
              />
            </CardContent>
          </Card>
        </div>
      </PageBody>

      {/* Version history sheet */}
      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Version history</SheetTitle>
          </SheetHeader>
          <SheetBody>
            {revisionsLoading ? (
              <div className="flex justify-center py-12">
                <Spinner size="md" />
              </div>
            ) : revisions.length ? (
              <ul className="space-y-3">
                {revisions.map((rev) => (
                  <li
                    key={rev.id}
                    className="rounded-xl border border-gray-200 px-4 py-3 dark:border-dark-border/50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">v{rev.revision_number}</Badge>
                          <span className="text-sm text-muted-foreground">
                            {new Date(rev.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {rev.about_company.length} paragraph(s), {rev.what_we_offer.length} offer item(s)
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => restoreRevision(rev)}
                      >
                        Restore
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ClockIcon className="mb-3 h-10 w-10 text-gray-300" />
                <p className="font-medium text-charcoal dark:text-gray-100">No versions yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Save your configuration to create the first version.
                </p>
              </div>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </Page>
  );
}

export default RoleBuilderSettingsPage;
